import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Gift, X, CheckCircle } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'

interface Redemption {
  id: string
  created_at: string
  redeemed_at: string | null
  redemption_type: string | null
  full_name: string
  phone: string
  game_name: string
  prize_name: string
  prize_type: string
}

function RedeemDialog({ onClose }: { onClose: () => void }) {
  const [phone, setPhone] = useState('')
  const [code, setCode] = useState('')
  const [result, setResult] = useState<{ customer_name: string; prize_name: string } | null>(null)
  const [error, setError] = useState('')

  const redeem = useMutation({
    mutationFn: (body: { phone: string; code: string }) =>
      api.post('/discounts/redeem-instore', body).then(r => r.data),
    onSuccess: (data) => {
      setError('')
      setResult({ customer_name: data.customer_name, prize_name: data.prize_name })
    },
    onError: (e: any) => {
      setError(e.response?.data?.message ?? 'Redemption failed')
      setResult(null)
    },
  })

  const handleSubmit = (ev: React.FormEvent) => {
    ev.preventDefault()
    if (!phone.trim() || !code.trim()) return
    redeem.mutate({ phone: phone.trim(), code: code.trim().toUpperCase() })
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/70" onClick={onClose} />
      <div className="relative bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <Gift size={18} className="text-[#c41e3a]" />
            <h2 className="text-white font-semibold">Redeem Prize In-Store</h2>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-white transition-colors">
            <X size={18} />
          </button>
        </div>

        {result ? (
          <div className="text-center py-4 space-y-3">
            <CheckCircle size={48} className="text-green-400 mx-auto" />
            <p className="text-white font-semibold text-lg">Prize Redeemed!</p>
            <p className="text-gray-400 text-sm">
              <span className="text-white">{result.customer_name}</span> has redeemed:
            </p>
            <p className="text-[#c41e3a] font-semibold">{result.prize_name}</p>
            <button
              onClick={onClose}
              className="mt-2 w-full px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
            >
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <p className="text-gray-500 text-sm">
              Enter the customer's phone number and the 6-digit code shown on their screen.
            </p>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Customer phone number</label>
              <input
                type="tel"
                value={phone}
                onChange={e => setPhone(e.target.value)}
                placeholder="+962 7X XXX XXXX"
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-400 mb-1">Redemption code</label>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value.toUpperCase())}
                placeholder="6-digit code"
                maxLength={10}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white font-mono tracking-widest placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#c41e3a]"
              />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <button
              type="submit"
              disabled={redeem.isPending || !phone.trim() || !code.trim()}
              className="w-full px-4 py-2.5 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830] disabled:opacity-50"
            >
              {redeem.isPending ? 'Verifying…' : 'Redeem Prize'}
            </button>
          </form>
        )}
      </div>
    </div>
  )
}

const PRIZE_TYPE_LABELS: Record<string, string> = {
  physical_item: 'Physical Item',
  free_delivery: 'Free Delivery',
}

export default function Prizes() {
  const [showDialog, setShowDialog] = useState(false)
  const [filterRedeemed, setFilterRedeemed] = useState<'all' | 'pending' | 'redeemed'>('all')
  const [filterType, setFilterType] = useState<'all' | 'physical_item' | 'free_delivery'>('all')
  const qc = useQueryClient()

  const { data, isLoading } = useQuery<{ redemptions: Redemption[] }>({
    queryKey: ['prize-redemptions'],
    queryFn: () => api.get('/discounts/redemptions').then(r => r.data),
  })

  const redemptions = (data?.redemptions ?? []).filter(r => {
    if (filterRedeemed === 'pending' && r.redeemed_at) return false
    if (filterRedeemed === 'redeemed' && !r.redeemed_at) return false
    if (filterType !== 'all' && r.prize_type !== filterType) return false
    return true
  })

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-5">
        <h1 className="text-lg font-semibold text-white">Prize Redemptions</h1>
        <button
          onClick={() => { setShowDialog(true); qc.invalidateQueries({ queryKey: ['prize-redemptions'] }) }}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#c41e3a] text-white text-sm font-semibold hover:bg-[#a01830]"
        >
          <Gift size={15} />
          Redeem In-Store
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-5">
        {(['all', 'pending', 'redeemed'] as const).map(f => (
          <button
            key={f}
            onClick={() => setFilterRedeemed(f)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterRedeemed === f
                ? 'bg-[#c41e3a]/20 text-[#c41e3a]'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}
        <div className="w-px bg-[#2e2e2e] mx-1" />
        {(['all', 'physical_item', 'free_delivery'] as const).map(t => (
          <button
            key={t}
            onClick={() => setFilterType(t)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              filterType === t
                ? 'bg-blue-500/20 text-blue-400'
                : 'bg-white/5 text-gray-400 hover:text-white'
            }`}
          >
            {t === 'all' ? 'All Types' : PRIZE_TYPE_LABELS[t]}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-gray-500 text-sm">Loading…</div>
      ) : redemptions.length === 0 ? (
        <div className="text-center py-16 text-gray-600">
          <Gift size={40} className="mx-auto mb-3 opacity-30" />
          <p className="text-sm">No prize redemptions yet</p>
        </div>
      ) : (
        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-600 border-b border-[#2e2e2e]">
                <th className="px-4 py-3 text-left font-medium">Customer</th>
                <th className="px-4 py-3 text-left font-medium">Prize</th>
                <th className="px-4 py-3 text-left font-medium">Game</th>
                <th className="px-4 py-3 text-left font-medium">Won</th>
                <th className="px-4 py-3 text-left font-medium">Status</th>
                <th className="px-4 py-3 text-left font-medium">Redeemed</th>
              </tr>
            </thead>
            <tbody>
              {redemptions.map(r => (
                <tr key={r.id} className="border-b border-[#222]">
                  <td className="px-4 py-3">
                    <p className="text-white">{r.full_name}</p>
                    <p className="text-gray-500 text-xs">{r.phone}</p>
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white">{r.prize_name}</p>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                      r.prize_type === 'free_delivery'
                        ? 'bg-blue-500/15 text-blue-400'
                        : 'bg-purple-500/15 text-purple-400'
                    }`}>
                      {PRIZE_TYPE_LABELS[r.prize_type] ?? r.prize_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-400 text-xs">{r.game_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                  <td className="px-4 py-3">
                    {r.redeemed_at ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-xs font-medium">
                        <CheckCircle size={10} /> Redeemed
                      </span>
                    ) : (
                      <span className="inline-flex px-2 py-0.5 rounded-full bg-yellow-500/15 text-yellow-400 text-xs font-medium">
                        Pending
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                    {r.redeemed_at
                      ? `${formatDateTime(r.redeemed_at)} · ${r.redemption_type?.replace('_', ' ')}`
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showDialog && <RedeemDialog onClose={() => setShowDialog(false)} />}
    </div>
  )
}
